/**
 * PageMeta — lightweight per-page SEO manager.
 *
 * We deliberately avoid react-helmet-async to keep the dependency tree small.
 * This component mutates document head on mount + restores on unmount.
 *
 * Usage:
 *   <PageMeta
 *     title="Grade Prediction — MyAE"
 *     description="…"
 *     canonical="https://academicexaminer.online/features/grade-prediction"
 *     jsonLd={[{...}, {...}]}
 *   />
 */
import { useEffect } from "react";

const SITE = "https://academicexaminer.online";

function upsertMeta(attrName, attrValue, content) {
  if (content == null) return null;
  let tag = document.head.querySelector(`meta[${attrName}="${attrValue}"]`);
  const created = !tag;
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attrName, attrValue);
    document.head.appendChild(tag);
  }
  const prev = tag.getAttribute("content");
  tag.setAttribute("content", content);
  return { tag, prev, created };
}

function upsertLink(rel, href) {
  if (!href) return null;
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  const created = !tag;
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  const prev = tag.getAttribute("href");
  tag.setAttribute("href", href);
  return { tag, prev, created };
}

export default function PageMeta({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  jsonLd = [],
  robots = "index,follow,max-image-preview:large",
}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    // Absolute canonical
    const absCanonical = canonical && canonical.startsWith("http") ? canonical : (canonical ? `${SITE}${canonical}` : null);

    const changes = [
      upsertMeta("name", "description", description),
      upsertMeta("name", "robots", robots),
      upsertMeta("property", "og:title", ogTitle || title),
      upsertMeta("property", "og:description", ogDescription || description),
      upsertMeta("property", "og:url", absCanonical),
      upsertMeta("property", "og:image", ogImage || `${SITE}/favicon.svg`),
      upsertMeta("name", "twitter:title", ogTitle || title),
      upsertMeta("name", "twitter:description", ogDescription || description),
      upsertMeta("name", "twitter:image", ogImage || `${SITE}/favicon.svg`),
      upsertLink("canonical", absCanonical),
    ].filter(Boolean);

    // Structured data — inject fresh, then remove on unmount
    const scripts = [];
    for (const block of jsonLd) {
      if (!block) continue;
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-myae-jsonld", "page");
      script.text = JSON.stringify(block);
      document.head.appendChild(script);
      scripts.push(script);
    }

    return () => {
      document.title = prevTitle;
      for (const c of changes) {
        if (!c) continue;
        if (c.created) c.tag.remove();
        else if (c.tag && c.prev != null) {
          const attr = c.tag.tagName === "LINK" ? "href" : "content";
          c.tag.setAttribute(attr, c.prev);
        }
      }
      for (const s of scripts) s.remove();
    };
  }, [title, description, canonical, ogTitle, ogDescription, ogImage, JSON.stringify(jsonLd), robots]);

  return null;
}
